import { useMemo, useCallback } from 'react'

import type {
  BillingInterval,
  PlanOption,
  PlanPricingTier,
} from '../../../gql/graphql'

import type { PlanCalculation } from '../types'
import {
  calculateTierPrice,
  calculateAllPlanPricing,
  formatPriceSimple,
} from '../utils/pricingCalculations'
import type { EnhancedPlanCalculation } from '../utils/pricingCalculations'

export interface UsePricingCalculationsProps {
  planOptions: PlanOption[]
  channelCount: number
  billingCycle: BillingInterval
}

export interface UsePricingCalculationsReturn {
  getCalculationForPlan: (planId: string) => EnhancedPlanCalculation | undefined
  getAllCalculations: () => Record<string, EnhancedPlanCalculation>
  calculateTierPrice: (
    pricingTiers: PlanPricingTier[],
    channelCount: number,
  ) => PlanCalculation
  formatPrice: (price: number, currency?: string) => string
}

/**
 * Custom hook for handling enhanced pricing calculations with tiered pricing support.
 *
 * This hook provides utilities to:
 * - Handle tiered pricing structures with PlanPricingTier
 * - Support different billing intervals (monthly/yearly)
 * - Calculate price per channel
 * - Format prices for display
 *
 * @param props - Configuration object containing plan options and channel count
 * @returns Object with pricing calculation functions and utilities
 *
 * @example
 * ```typescript
 * const { getCalculationForPlan, formatPrice } = usePricingCalculations({
 *   planOptions: availablePlans,
 *   channelCount: 5
 * })
 *
 * const calculation = getCalculationForPlan('essentials')
 * const priceText = formatPrice(calculation.totalPrice)
 * ```
 */
export function usePricingCalculations({
  planOptions,
  channelCount,
  billingCycle,
}: UsePricingCalculationsProps): UsePricingCalculationsReturn {
  const calculateTierPriceCallback = useCallback(calculateTierPrice, [])
  const formatPrice = useCallback(formatPriceSimple, [])

  const allCalculations = useMemo(() => {
    return calculateAllPlanPricing(planOptions, channelCount, billingCycle)
  }, [planOptions, channelCount, billingCycle])

  const getCalculationForPlan = useCallback(
    (planId: string): EnhancedPlanCalculation | undefined => {
      return allCalculations[planId]
    },
    [allCalculations],
  )

  const getAllCalculations = useCallback(
    () => allCalculations,
    [allCalculations],
  )

  return {
    getCalculationForPlan,
    getAllCalculations,
    calculateTierPrice: calculateTierPriceCallback,
    formatPrice,
  }
}
